import type {
  CheckStatus,
  FreshnessState,
  HistoryPoint,
  OperationalStatus,
} from "../types";

export const STATUS_COPY: Record<OperationalStatus, { label: string; short: string }> = {
  operational: { label: "Todos los sistemas operativos", short: "Operativo" },
  degraded: { label: "Algunos sistemas requieren atención", short: "Atención" },
  outage: { label: "Hay una interrupción activa", short: "Incidente" },
  maintenance: { label: "Mantenimiento en curso", short: "Mantenimiento" },
  unknown: { label: "Estado por confirmar", short: "Sin datos" },
};

export const CHECK_COPY: Record<CheckStatus, string> = {
  pass: "Correcto",
  warn: "Revisar",
  fail: "Fallando",
  unknown: "Sin verificar",
};

export const FRESHNESS_COPY: Record<FreshnessState, string> = {
  fresh: "Telemetría al día",
  stale: "Telemetría retrasada",
  no_telemetry: "Sin telemetría reciente",
};

export function clamp(value: number, min = 0, max = 100): number {
  return Math.min(max, Math.max(min, Number.isFinite(value) ? value : 0));
}

export function formatPercent(value: number, digits = 0): string {
  return `${new Intl.NumberFormat("es-CO", {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(clamp(value))}%`;
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const unit = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** unit;
  return `${new Intl.NumberFormat("es-CO", {
    maximumFractionDigits: value >= 10 || unit === 0 ? 0 : 1,
  }).format(value)} ${units[unit]}`;
}

export function formatDuration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return "—";
  if (seconds < 60) return `${Math.floor(seconds)} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ${minutes % 60} min`;
  const days = Math.floor(hours / 24);
  return `${days} d ${hours % 24} h`;
}

export function formatRelativeTime(iso: string, now = Date.now()): string {
  const timestamp = Date.parse(iso);
  if (!Number.isFinite(timestamp)) return "hora desconocida";
  const seconds = Math.max(0, Math.round((now - timestamp) / 1000));
  if (seconds < 10) return "ahora mismo";
  if (seconds < 60) return `hace ${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  return `hace ${Math.floor(hours / 24)} d`;
}

export function formatAbsoluteTime(iso: string): string {
  const timestamp = Date.parse(iso);
  if (!Number.isFinite(timestamp)) return "Fecha desconocida";
  return new Intl.DateTimeFormat("es-CO", {
    dateStyle: "medium",
    timeStyle: "medium",
    timeZone: "America/Bogota",
  }).format(timestamp);
}

export function calculateAvailability(history: HistoryPoint[]): number | null {
  const known = history.filter((point) => point.overallStatus !== "unknown");
  if (known.length < 2) return null;
  const available = known.filter(
    (point) => point.overallStatus === "operational" || point.overallStatus === "degraded",
  ).length;
  return (available / known.length) * 100;
}

export function makeSparkline(values: number[], width = 240, height = 72): string {
  if (values.length === 0) return "";
  const safe = values.map((value) => clamp(value));
  const divisor = Math.max(1, safe.length - 1);
  return safe
    .map((value, index) => {
      const x = (index / divisor) * width;
      const y = height - (value / 100) * (height - 8) - 4;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
}
